import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { CometIcon, PlanetIcon, PlusIcon, RadarIcon } from '../../icons/ui-icons';
import { Badge } from '../primitives/Badge';

export type GalaxySlotKind = 'player' | 'npc' | 'bot' | 'empty' | 'event';

export interface GalaxySlot {
  position: number;
  occupantKind: GalaxySlotKind;
  /** True only for the viewer's own 'player' slot. */
  isMine?: boolean;
}

interface SlotMeta {
  label: string;
  icon: ReactNode;
}

const SLOT_META: Record<GalaxySlotKind, SlotMeta> = {
  player: { label: 'Spieler-Kolonie', icon: <PlanetIcon size={20} /> },
  npc: { label: 'NPC-Kolonie', icon: <RadarIcon size={20} /> },
  // No real fake-player state is linked to this slot yet (galaxy plan
  // Milestone D — see docs/PROGRESS.md); "in Gründung" is honest about that
  // rather than implying a colony that isn't actually there yet.
  bot: { label: 'Kolonie in Gründung', icon: <PlanetIcon size={20} /> },
  empty: { label: 'Freier Platz', icon: <PlusIcon size={20} /> },
  event: { label: 'Ereignis', icon: <CometIcon size={20} /> },
};

export interface PlanetSlotCardProps extends GalaxySlot {
  className?: string;
  action?: ReactNode;
}

/** One position in a system (galaxy plan) — own colony, another player's,
 * an NPC/fake-player colony, an empty spot, or a time-limited event. */
export function PlanetSlotCard({ position, occupantKind, isMine, className, action }: PlanetSlotCardProps) {
  const meta = SLOT_META[occupantKind];
  const mine = occupantKind === 'player' && isMine;
  const empty = occupantKind === 'empty';

  return (
    <div
      role="listitem"
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-control border p-3 text-center',
        mine
          ? 'border-silicate/50 bg-silicate/[0.08]'
          : empty
            ? 'border-dashed border-edge-subtle bg-transparent'
            : 'border-edge bg-surface-2',
        className,
      )}
    >
      <span className="font-display text-label-sm uppercase text-content-muted">Platz {position}</span>
      <span
        aria-hidden
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-full',
          mine ? 'bg-silicate/15 text-silicate-bright' : empty ? 'text-content-muted/60' : 'bg-surface-3 text-content-muted',
        )}
      >
        {meta.icon}
      </span>
      <span className={cn('text-label-sm font-medium', empty ? 'text-content-muted' : 'text-content')}>
        {mine ? 'Deine Kolonie' : meta.label}
      </span>
      {mine ? (
        <Badge accent="silicate" size="sm">
          DU
        </Badge>
      ) : null}
      {action ? <div className="mt-1 w-full">{action}</div> : null}
    </div>
  );
}

export interface SystemGridProps {
  slots: GalaxySlot[];
  className?: string;
  renderAction?: (slot: GalaxySlot) => ReactNode;
}

/**
 * The 10-position system grid (galaxy plan) — 2 columns on phones (5 rows,
 * no horizontal scroll needed), 5 columns from `sm` up (2 rows). First
 * real grid-shaped layout in the design system; `StatGrid` is a generic
 * KPI grid and isn't a fit for cards this rich.
 */
export function SystemGrid({ slots, className, renderAction }: SystemGridProps) {
  const sorted = [...slots].sort((a, b) => a.position - b.position);
  return (
    <div role="list" aria-label="Systemplätze" className={cn('grid grid-cols-2 gap-2 sm:grid-cols-5', className)}>
      {sorted.map((slot) => (
        <PlanetSlotCard key={slot.position} {...slot} action={renderAction?.(slot)} />
      ))}
    </div>
  );
}
